import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faCheck,
    faImage,
    faVideo,
    faMicrophone,
    faMagnifyingGlass,
    faSpinner,
    faCheckCircle,
    faSquare,
    faCheckSquare,
    faCloudArrowUp,
    faFilter
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import Tippy from '@tippyjs/react';
import { galleryService } from '../../services/galleryService';
import apiHelper from '../../utils/apiHelper';
import axios from 'axios';
import './EnhancedGalleryBrowser.css';

const MediaPreview = React.memo(({ item, isSelected, onToggleSelect }) => {
    const [mediaUrl, setMediaUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const isVideo = item.mimeType?.startsWith('video/');
    const isAudio = item.mimeType?.startsWith('audio/');

    const loadMedia = async () => {
        if (mediaUrl || isLoading) return;
        setIsLoading(true);
        setError(null);
        
        try {
            const headers = await apiHelper.getHeaders('graph/getMediaContent');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/graph/getMediaContent/${item.name}`,
                {
                    headers,
                    responseType: 'blob'
                }
            );
            
            const url = URL.createObjectURL(response.data);
            setMediaUrl(url);
        } catch (err) {
            setError('Failed to load media');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isVideo && !isAudio) {
            loadMedia();
        }
        
        return () => {
            if (mediaUrl) {
                URL.revokeObjectURL(mediaUrl);
            }
        };
    }, []);

    const getMediaTypeIcon = () => {
        if (isVideo) return faVideo;
        if (isAudio) return faMicrophone;
        return faImage;
    };

    return (
        <div 
            className={`gallery-item ${isSelected ? 'selected' : ''}`}
            onClick={() => onToggleSelect(item)}
        >
            <div className="selection-indicator">
                <FontAwesomeIcon 
                    icon={isSelected ? faCheckSquare : faSquare} 
                    className={isSelected ? 'selected-icon' : 'unselected-icon'}
                />
            </div>

            <div className="media-preview">
                {isLoading ? (
                    <div className="loading-preview">
                        <FontAwesomeIcon icon={faSpinner} spin />
                    </div>
                ) : error ? (
                    <div className="error-preview">
                        <FontAwesomeIcon icon={getMediaTypeIcon()} />
                        <span>Error</span>
                    </div>
                ) : isVideo ? (
                    <div className="video-preview">
                        <FontAwesomeIcon icon={faVideo} className="media-type-icon" />
                        <span className="media-type-label">Video</span>
                    </div>
                ) : isAudio ? (
                    <div className="audio-preview">
                        <FontAwesomeIcon icon={faMicrophone} className="media-type-icon" />
                        <span className="media-type-label">Audio</span>
                    </div>
                ) : (
                    <img 
                        src={mediaUrl}
                        alt={item.title || item.name}
                        className="preview-thumbnail"
                        onError={() => setError('Failed to load image')}
                    />
                )}
            </div>

            <div className="item-info">
                <h4 className="item-title" title={item.title || item.name || 'Unknown'}>
                    {item.title || item.name || 'Unknown Item'}
                </h4>
                <div className="item-metadata">
                    <span className="media-type">
                        <FontAwesomeIcon icon={getMediaTypeIcon()} />
                        {isVideo ? 'Video' : isAudio ? 'Audio' : 'Image'}
                    </span>
                    <span className="file-size">
                        {item.size ? (item.size / 1024 / 1024).toFixed(1) + ' MB' : 
                         item.Size ? (item.Size / 1024 / 1024).toFixed(1) + ' MB' : 
                         'Size unknown'}
                    </span>
                </div>
            </div>
        </div>
    );
});

const EnhancedGalleryBrowser = ({ isOpen, onClose, onSelectItems, galleries: propGalleries }) => {
    const [galleries, setGalleries] = useState(propGalleries || []);
    const [selectedGallery, setSelectedGallery] = useState(null);
    const [galleryItems, setGalleryItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [isLoadingGalleries, setIsLoadingGalleries] = useState(false);
    const [isLoadingItems, setIsLoadingItems] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [mediaTypeFilter, setMediaTypeFilter] = useState('all');

    // Sync propGalleries with galleries state and preload counts
    useEffect(() => {
        if (propGalleries && propGalleries.length > 0) {
            setGalleries(propGalleries);
            // Preload item counts for all galleries
            preloadGalleryCounts(propGalleries);
        }
    }, [propGalleries]);

    // Load galleries if not provided
    useEffect(() => {
        if (isOpen && (!propGalleries || propGalleries.length === 0)) {
            loadGalleries();
        }
    }, [isOpen, propGalleries]);

    // Auto-select first gallery
    useEffect(() => {
        if (galleries.length > 0 && !selectedGallery) {
            setSelectedGallery(galleries[0]);
        }
    }, [galleries]);

    // Load gallery items when gallery changes
    useEffect(() => {
        if (selectedGallery) {
            // Clear previous gallery items first
            setGalleryItems([]);
            setSelectedItems(new Set());
            loadGalleryItems(selectedGallery.id);
        }
    }, [selectedGallery]);

    const loadGalleries = async () => {
        setIsLoadingGalleries(true);
        try {
            const galleriesData = await galleryService.getGalleries();
            setGalleries(galleriesData || []);
        } catch (error) {
            toast.error('Failed to load galleries');
        } finally {
            setIsLoadingGalleries(false);
        }
    };

    // Preload item counts for all galleries
    const preloadGalleryCounts = async (galleriesList) => {
        try {
            // Fetch counts for all galleries in parallel
            const countPromises = galleriesList.map(async (gallery) => {
                try {
                    const items = await galleryService.getGalleryItems(gallery.id);
                    return {
                        id: gallery.id,
                        itemCount: items?.length || 0
                    };
                } catch (error) {
                    return {
                        id: gallery.id,
                        itemCount: 0
                    };
                }
            });

            const counts = await Promise.all(countPromises);

            // Update galleries with their item counts
            setGalleries(prevGalleries => 
                prevGalleries.map(gallery => {
                    const countInfo = counts.find(c => c.id === gallery.id);
                    return {
                        ...gallery,
                        itemCount: countInfo?.itemCount || 0
                    };
                })
            );
        } catch (error) {
            // Silent fail for preloading
        }
    };

    const loadGalleryItems = async (galleryId) => {
        setIsLoadingItems(true);
        setGalleryItems([]); // Clear items immediately
        try {
            const items = await galleryService.getGalleryItems(galleryId);
            setGalleryItems(items || []);
            
            // Update the item count for the current gallery
            setGalleries(prevGalleries => 
                prevGalleries.map(gallery => 
                    gallery.id === galleryId 
                        ? { ...gallery, itemCount: items?.length || 0 }
                        : gallery
                )
            );
        } catch (error) {
            toast.error(`Failed to load gallery items: ${error.message}`);
        } finally {
            setIsLoadingItems(false);
        }
    };

    const handleToggleItemSelect = useCallback((item) => {
        const itemId = item.id || item.Id;
        setSelectedItems(prev => {
            const newSelected = new Set(prev);
            if (newSelected.has(itemId)) {
                newSelected.delete(itemId);
            } else {
                newSelected.add(itemId);
            }
            return newSelected;
        });
    }, []);

    const handleSelectAll = () => {
        if (selectedItems.size === filteredItems.length) {
            setSelectedItems(new Set());
        } else {
            setSelectedItems(new Set(filteredItems.map(item => item.id || item.Id)));
        }
    };

    const handleConfirmSelection = () => {
        const selectedItemsArray = galleryItems.filter(item => selectedItems.has(item.id || item.Id));
        onSelectItems(selectedItemsArray);
    };

    // Filter items based on search and media type, and deduplicate
    const filteredItems = galleryItems
        .filter((item, index, array) => {
            // Deduplicate by filename/id - keep only the first occurrence
            return array.findIndex(i => (i.id || i.Id) === (item.id || item.Id)) === index;
        })
        .filter(item => {
            const matchesSearch = !searchTerm || 
                item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.name?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesMediaType = mediaTypeFilter === 'all' || 
                (mediaTypeFilter === 'image' && item.mimeType?.startsWith('image/')) ||
                (mediaTypeFilter === 'video' && item.mimeType?.startsWith('video/')) ||
                (mediaTypeFilter === 'audio' && item.mimeType?.startsWith('audio/'));

            return matchesSearch && matchesMediaType;
        });

    if (!isOpen) {
        return null;
    }

    return (
        <div 
            className="enhanced-gallery-browser-overlay"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
            }}
        >
            <div 
                className="enhanced-gallery-browser"
                style={{
                    width: '98vw',
                    height: '95vh',
                    maxWidth: 'none',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <div 
                    className="browser-header"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '1.5rem 2rem',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                    }}
                >
                    <div className="header-left">
                        <h2>
                            <FontAwesomeIcon icon={faCloudArrowUp} className="me-2" />
                            Browse All Galleries
                        </h2>
                        <span className="subtitle">Select items to publish to Media Centre</span>
                    </div>
                    <div className="header-right">
                        <button className="close-btn" onClick={onClose}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>

                {/* Gallery Tabs */}
                <div className="gallery-tabs">
                    {isLoadingGalleries ? (
                        <div className="loading-tabs">
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Loading galleries...
                        </div>
                    ) : (
                        galleries.map(gallery => (
                            <button
                                key={gallery.id}
                                className={`gallery-tab ${selectedGallery?.id === gallery.id ? 'active' : ''}`}
                                onClick={() => setSelectedGallery(gallery)}
                            >
                                <FontAwesomeIcon icon={faImage} className="me-1" />
                                {gallery.title}
                                <span className="item-count">({gallery.itemCount || 0})</span>
                            </button>
                        ))
                    )}
                </div>

                {/* Toolbar */}
                <div className="browser-toolbar">
                    <div className="toolbar-left">
                        <div className="search-box">
                            <FontAwesomeIcon icon={faMagnifyingGlass} />
                            <input
                                type="text"
                                placeholder="Search items..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <select
                            className="media-type-filter"
                            value={mediaTypeFilter}
                            onChange={(e) => setMediaTypeFilter(e.target.value)}
                        >
                            <option value="all">All Types</option>
                            <option value="image">Images</option>
                            <option value="video">Videos</option>
                            <option value="audio">Audio</option>
                        </select>
                    </div>
                    <div className="toolbar-right">
                        <button 
                            className="select-all-btn"
                            onClick={handleSelectAll}
                        >
                            <FontAwesomeIcon icon={selectedItems.size === filteredItems.length ? faCheckSquare : faSquare} />
                            {selectedItems.size === filteredItems.length ? 'Deselect All' : 'Select All'}
                        </button>
                        <span className="selection-count">
                            {selectedItems.size} of {filteredItems.length} selected
                        </span>
                    </div>
                </div>

                {/* Content */}
                <div 
                    className="enhanced-browser-content"
                    style={{
                        flex: '1 1 auto',
                        padding: '20px',
                        overflowY: 'auto',
                        backgroundColor: '#f8f9fa',
                        width: '100%',
                        boxSizing: 'border-box'
                    }}
                >
                    {isLoadingItems ? (
                        <div className="loading-content">
                            <FontAwesomeIcon icon={faSpinner} spin className="loading-icon" />
                            <p>Loading gallery items...</p>
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="empty-content">
                            <FontAwesomeIcon icon={faImage} className="empty-icon" />
                            <h3>No Items Found</h3>
                            <p>This gallery is empty or no items match your filters.</p>
                        </div>
                    ) : (
                        <div 
                            key={`gallery-grid-${selectedGallery?.id || 'none'}`}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                gap: '20px',
                                paddingBottom: '20px',
                                width: '100%',
                                justifyItems: 'start',
                                alignItems: 'start'
                            }}
                        >
                            {filteredItems.map((item, index) => {
                                // Create truly unique key combining gallery ID, item ID, and filename
                                const uniqueKey = `${selectedGallery?.id || 'unknown'}-${item.id || item.Id || item.name || item.fileName || index}`;
                                
                                return (
                                    <div 
                                        key={uniqueKey}
                                        style={{
                                            width: '100%',
                                            maxWidth: '400px',
                                            minWidth: '280px',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}
                                    >
                                                                            <MediaPreview
                                        key={`media-${uniqueKey}`}
                                        item={item}
                                        isSelected={selectedItems.has(item.id || item.Id)}
                                        onToggleSelect={handleToggleItemSelect}
                                    />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="browser-footer">
                    <div className="footer-left">
                        <span className="selection-summary">
                            {selectedItems.size > 0 
                                ? `${selectedItems.size} item(s) selected for publishing`
                                : 'Select items to publish to Media Centre'
                            }
                        </span>
                    </div>
                    <div className="footer-right">
                        <button className="cancel-btn" onClick={onClose}>
                            Cancel
                        </button>
                        <button 
                            className="publish-btn"
                            onClick={handleConfirmSelection}
                            disabled={selectedItems.size === 0}
                        >
                            <FontAwesomeIcon icon={faCloudArrowUp} className="me-1" />
                            Publish {selectedItems.size > 0 ? `${selectedItems.size} Item(s)` : 'Selected'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnhancedGalleryBrowser; 